import React, { useState, useEffect } from 'react';
import { consolidatedRptApi } from '../services/api';
import './IntercompanyPanel.css';

/**
 * IntercompanyPanel - Consolidated RPT across all EL_SAP_B1 entities
 * 
 * This panel allows users to:
 * 1. Generate consolidated RPT report for all EL_SAP_B1 entities
 * 2. View and download previously generated consolidated reports
 * 3. Configure options like date range and whether to use existing RPT data
 */
function IntercompanyPanel() {
  // Form state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [useExisting, setUseExisting] = useState(true);
  const [useLLMClassification, setUseLLMClassification] = useState(false);
  
  // UI state
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  
  // Reports list
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Set default dates on mount
  useEffect(() => {
    // Default to current fiscal year (April to March)
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    
    let fiscalYearStart, fiscalYearEnd;
    if (currentMonth >= 4) {
      // We're in the current fiscal year (Apr-Dec)
      fiscalYearStart = `${currentYear}-04-01`;
      fiscalYearEnd = `${currentYear}-12-31`;
    } else {
      // We're in the previous fiscal year (Jan-Mar)
      fiscalYearStart = `${currentYear - 1}-04-01`;
      fiscalYearEnd = `${currentYear}-03-31`;
    }
    
    setStartDate(fiscalYearStart);
    setEndDate(fiscalYearEnd);
    
    // Load existing reports
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoadingReports(true);
    try {
      const data = await consolidatedRptApi.listReports();
      setReports(data.reports || []);
    } catch (err) {
      console.error('Error loading reports:', err);
    } finally {
      setLoadingReports(false);
    }
  };

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    setGenerating(true);
    setError(null);
    setResult(null);

    try {
      const response = await consolidatedRptApi.generate({
        start_date: startDate,
        end_date: endDate,
        use_existing: useExisting,
        use_llm_classification: useLLMClassification
      });

      if (response.success) {
        setResult(response);
        // Refresh reports list
        loadReports();
      } else {
        setError(response.error || 'Failed to generate consolidated RPT');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while generating the report');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (report) => {
    try {
      const blob = await consolidatedRptApi.download(report.period, report.timestamp);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rpt_consolidated_${report.period}_${report.timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download report: ' + err.message);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return isoString;
    }
  };

  return (
    <div className="intercompany-panel">
      <div className="panel-header">
        <h2>🔗 Intercompany Consolidated RPT</h2>
        <p className="panel-description">
          Generate a consolidated Related Party Transactions report across all EL_SAP_B1 entities.
          This combines RPT Final data from all entities into a single Excel file.
        </p>
      </div>

      {/* Generation Form */}
      <div className="generation-section">
        <h3>Generate New Report</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="startDate">Start Date</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={generating}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="endDate">End Date</label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={generating}
            />
          </div>
        </div>

        <div className="form-row options-row">
          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={useExisting}
                onChange={(e) => setUseExisting(e.target.checked)}
                disabled={generating}
              />
              <span className="checkbox-label">
                Use existing RPT if available
                <span className="tooltip" title="If checked, will use previously generated RPT from S3. If unchecked, will regenerate RPT for all entities.">ℹ️</span>
              </span>
            </label>
          </div>
          
          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={useLLMClassification}
                onChange={(e) => setUseLLMClassification(e.target.checked)}
                disabled={generating}
              />
              <span className="checkbox-label">
                Use AI/LLM for nature classification
                <span className="tooltip" title="Enable Claude AI for classifying transaction natures. More accurate but slower and has API costs.">ℹ️</span>
              </span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button
            className="btn-primary btn-generate"
            onClick={handleGenerate}
            disabled={generating || !startDate || !endDate}
          >
            {generating ? (
              <>
                <span className="spinner"></span>
                Generating...
              </>
            ) : (
              <>🚀 Generate Consolidated RPT</>
            )}
          </button>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">❌</span>
            {error}
          </div>
        )}

        {/* Generation Result */}
        {result && (
          <div className="result-section">
            <h4>✅ Report Generated Successfully</h4>
            
            <div className="result-summary">
              <div className="summary-card">
                <span className="summary-value">{result.summary?.entities_processed || 0}</span>
                <span className="summary-label">Entities Processed</span>
              </div>
              <div className="summary-card">
                <span className="summary-value">{result.summary?.total_rows || 0}</span>
                <span className="summary-label">Total RPT Rows</span>
              </div>
              <div className="summary-card">
                <span className="summary-value">{result.summary?.unique_natures || 0}</span>
                <span className="summary-label">Unique Natures</span>
              </div>
              <div className="summary-card">
                <span className="summary-value">{result.summary?.entities_with_errors || 0}</span>
                <span className="summary-label">Errors</span>
              </div>
            </div>

            {result.entity_results && result.entity_results.length > 0 && (
              <div className="entity-results">
                <h5>Entity Details:</h5>
                <div className="entity-list">
                  {result.entity_results.map((entity, idx) => (
                    <div key={idx} className={`entity-item ${entity.status}`}>
                      <span className="entity-name">{entity.description || entity.entity}</span>
                      <span className="entity-rows">{entity.row_count} rows</span>
                      <span className="entity-source">({entity.source})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.errors && result.errors.length > 0 && (
              <div className="errors-list">
                <h5>⚠️ Errors:</h5>
                {result.errors.map((err, idx) => (
                  <div key={idx} className="error-item">
                    <span className="error-entity">{err.entity}:</span>
                    <span className="error-message">{err.error}</span>
                  </div>
                ))}
              </div>
            )}

            <button
              className="btn-secondary btn-download"
              onClick={() => handleDownload({ period: result.period, timestamp: result.timestamp })}
            >
              📥 Download Report
            </button>
          </div>
        )}
      </div>

      {/* Previous Reports */}
      <div className="reports-section">
        <div className="reports-header">
          <h3>📁 Previous Reports</h3>
          <button
            className="btn-icon btn-refresh"
            onClick={loadReports}
            disabled={loadingReports}
            title="Refresh list"
          >
            🔄
          </button>
        </div>

        {loadingReports ? (
          <div className="loading-reports">Loading reports...</div>
        ) : reports.length === 0 ? (
          <div className="no-reports">
            No consolidated reports found. Generate one above to get started.
          </div>
        ) : (
          <div className="reports-table-container">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Generated At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report, idx) => (
                  <tr key={idx}>
                    <td className="period-cell">
                      {report.period?.replace('_to_', ' to ').replace(/_/g, '-')}
                    </td>
                    <td className="date-cell">{formatDate(report.created_at)}</td>
                    <td className="actions-cell">
                      <button
                        className="btn-icon btn-download-small"
                        onClick={() => handleDownload(report)}
                        title="Download"
                      >
                        📥
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="info-section">
        <h4>ℹ️ About Consolidated RPT</h4>
        <ul>
          <li><strong>Scope:</strong> Processes all enabled EL_SAP_B1 entities (17 entities)</li>
          <li><strong>Output Columns:</strong> Reporting Entity, Transacting Entity, Nature of Transaction, Currency, Net Amount</li>
          <li><strong>Sheets:</strong> Summary, Consolidated RPT, By Reporting Entity, By Nature</li>
          <li><strong>Use Existing:</strong> When enabled, uses previously generated RPT from S3 for faster processing</li>
        </ul>
      </div>
    </div>
  );
}

export default IntercompanyPanel;
